import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, User, Phone, MapPin, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    postcode: "",
    telephone: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.password || !formData.postcode) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await register({
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        postcode: formData.postcode,
        telephone: formData.telephone
      });
      toast.success("Welcome to All WHUFC!");
      navigate("/subscription");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "Access to 100+ years of fixture history",
    "Complete player database with bios",
    "Exclusive fan forum access",
    "Cup competition archives",
    "Entertainment section",
    "Mobile-friendly experience"
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="register-page">
      {/* Left Panel - Benefits */}
      <div className="hidden lg:block lg:w-1/2 relative bg-[#7C2C3B]">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1508098682722-e99c43a406b2?auto=format&fit=crop&w=1200&q=80"
            alt="Stadium"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-lg">
            <h2 className="text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
              JOIN THE<br /><span className="text-[#2BAEE5]">WEST HAM FAMILY</span>
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Sign up and unlock everything a true Hammer needs.
            </p>

            <div className="space-y-4">
              {benefits.map((benefit) => (
                <div key={benefit} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#2BAEE5] flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white/90">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="mt-12 p-6 bg-white/10 backdrop-blur-sm rounded-xl">
              <div className="flex items-center gap-4">
                <div className="text-5xl font-bold text-[#F8D742]" style={{ fontFamily: 'Barlow Condensed' }}>FREE</div>
                <div className="text-white/80">
                  <div className="font-semibold">to join</div>
                  <div className="text-sm">Full access included</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-[#7C2C3B] flex items-center justify-center">
              <span className="text-white font-bold text-xl" style={{ fontFamily: 'Barlow Condensed' }}>WHF</span>
            </div>
            <span className="text-[#0F172A] font-bold text-2xl" style={{ fontFamily: 'Barlow Condensed' }}>WEST HAM FAMILY</span>
          </Link>

          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            CREATE ACCOUNT
          </h1>
          <p className="text-slate-600 mb-8">Join the Hammers community today</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="full_name" className="text-sm font-medium text-slate-700">Full Name *</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  className="pl-10"
                  data-testid="register-fullname"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email Address *</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="pl-10"
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="postcode" className="text-sm font-medium text-slate-700">Postcode *</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="postcode"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleChange}
                    placeholder="E20 1AA"
                    className="pl-10"
                    data-testid="register-postcode"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="telephone" className="text-sm font-medium text-slate-700">Telephone</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="telephone"
                    name="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder="07123456789"
                    className="pl-10"
                    data-testid="register-phone"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password *</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">Confirm Password *</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10"
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#7C2C3B] hover:bg-[#63232F] text-white font-bold py-6"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? "CREATING ACCOUNT..." : "CREATE ACCOUNT"}
            </Button>
          </form>

          {/* Emergent Google Auth — DO NOT HARDCODE THE URL, OR ADD ANY
              FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH. */}
          <div className="mt-6">
            <div className="relative flex items-center my-4">
              <div className="flex-grow border-t border-slate-200" />
              <span className="mx-3 text-xs text-slate-400 uppercase tracking-wide">or</span>
              <div className="flex-grow border-t border-slate-200" />
            </div>
            <button
              type="button"
              data-testid="register-google-btn"
              onClick={() => {
                const redirect = `${window.location.origin}/auth-callback`;
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
              }}
              className="w-full flex items-center justify-center gap-2 border border-slate-300 hover:border-slate-400 bg-white hover:bg-slate-50 text-slate-700 font-medium py-3 rounded-md transition"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </button>
          </div>

          <p className="mt-6 text-center text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="text-[#7C2C3B] font-semibold hover:underline" data-testid="register-login-link">
              Sign In
            </Link>
          </p>

          <p className="mt-4 text-center text-xs text-slate-500">
            By creating an account, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
